import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  CellInput,
  CellSelect,
  CodeChip,
  CurrencyMark,
  Distribution,
  EmptyState,
  EntityCell,
  Field,
  FieldGrid,
  FieldInput,
  FieldLocked,
  FieldValue,
  FilterBar,
  FilterControls,
  FilterSearch,
  FilterSelect,
  Form,
  FormActions,
  FormSection,
  Icon,
  IdentityCard,
  Legend,
  LegendItem,
  Lifecycle,
  LoadingState,
  MetaSeparator,
  MetaStrong,
  PageHeader,
  Panel,
  PanelFoot,
  PanelHead,
  PanelNote,
  PanelTag,
  RecordsCard,
  RecordsCount,
  RecordsFooter,
  RecordsScroll,
  RecordsToolbar,
  Ribbon,
  RowAction,
  RowActions,
  RowDetail,
  SelectField,
  SelectShell,
  Split,
  SplitMain,
  SplitSide,
  StatusChip,
  SummaryBadge,
  SummaryBand,
  SummaryCard,
  TabStrip,
  Table,
  Td,
  TextField,
  Th,
  Timeline,
  TimelineEvent,
  Totals,
  TotalsBanner,
  TotalsRow,
  Tr,
  statusTone,
} from '@tessma/design-system';
import type { AccentTone, LifecycleStep } from '@tessma/design-system';
import { Refusal, refusalText, formatDay, formatMoney, useAsync } from '../ui/async.js';
import type { PartiesGateway } from '../parties/gateway.js';
import { AddPartySheet } from './inline-create.js';
import { InvoiceDeliveriesPanel } from './document-screens.js';
import { daysBetween, fractionOf, initialsOf, sumMinor, today } from './figures.js';
import type {
  Account,
  AgeingBucket,
  CreditNote,
  DocumentSettings,
  DraftLine,
  FinanceCustomer,
  FinanceGateway,
  FinancePaymentTerm,
  Invoice,
  InvoiceDispute,
  InvoiceLine,
  SettlementDocument,
} from './gateway.js';

/**
 * ACCOUNTS RECEIVABLE — Screens 02, 04, 05, 06 and 07, converted from Stitch.
 *
 * **THE STRUCTURE IS THE REFERENCE'S.** Each screen below is a direct
 * conversion of its `code.html`, not an equivalent of its screenshot: a summary
 * band of the size the reference draws, the filter bar it draws, the dense
 * register it draws, the 12-column split it draws. The parts live in
 * `@tessma/design-system/records`, where each rule names the Tailwind class it
 * came from. The previous versions of these screens were assembled from the
 * generic patterns — a `Card`, a `CardHeader`, a `Table` — which is a different
 * screen carrying the same figures.
 *
 * **THE SHELL STILL NAMES EVERY PAGE.** No `<h1>` appears below. The
 * breadcrumb, the heading and the subtitle all come from the route table, so a
 * page renamed there is renamed everywhere. What the reference draws beside its
 * own `<h1>` — the actions, the record's name, its state — is kept, because that
 * is the record's identity rather than the page's.
 *
 * **THE UI DECIDES NOTHING.** It does not re-check a permission, compute a tax
 * or hide a record. Every figure came from the API, which computed it over the
 * authorised relation in SQL using exact integers. Where a screen groups rows
 * the server sent — the ageing pivot, a tab's count — it says so and uses
 * `BigInt`; where the server has the figure, the figure is rendered.
 *
 * **A DRAFT SHOWS NO TAX, AND THAT IS CORRECT.** Tax is computed at the TAX
 * POINT, which a draft does not have. The browser could multiply by 20% and be
 * wrong the day a rate changes; instead the draft's totals appear the moment the
 * server has computed them, and dashes stand where it has not.
 *
 * **AN ACTION THE CALLER MAY NOT TAKE IS HIDDEN, NEVER RELIED ON.** Hiding a
 * button is a courtesy to the person, not a control: the server refuses the same
 * request made by hand, with the same 403 or the same 404.
 *
 * **WHAT WAS OMITTED FROM THE REFERENCE, AND WHY (§4E).** Stitch draws export
 * menus, batch actions, bulk-select checkboxes, numbered pagination, a risk-tier
 * column, an AI ledger insight, a dunning cycle, a stock photograph of an office
 * and a credit-exposure model. None of those exists behind an API, and a control
 * that silently does nothing is worse than an absent one. Every omission is
 * named at the place it would have gone.
 */

function moneyOf(value: string, currency: string): string {
  return formatMoney(value, currency);
}

/** The frozen five-band ageing vocabulary, as the server names it. */
const BUCKET_LABELS: Readonly<Record<string, string>> = {
  not_due: 'Not due',
  current: 'Current',
  overdue_1_30: '1–30 days',
  overdue_31_60: '31–60 days',
  overdue_61_90: '61–90 days',
  overdue_90_plus: 'Over 90 days',
};

/** The reference's column order for the pivot, and the band band-tone map. */
const BUCKET_ORDER: readonly string[] = [
  'current',
  'overdue_1_30',
  'overdue_31_60',
  'overdue_61_90',
  'overdue_90_plus',
];

const BUCKET_TONE: Readonly<Record<string, AccentTone>> = {
  current: 'teal',
  overdue_1_30: 'info',
  overdue_31_60: 'warning',
  overdue_61_90: 'danger',
  overdue_90_plus: 'danger',
};

// ---- 1. Screen 04 — sales invoices ------------------------------------------

/**
 * THE SALES INVOICE REGISTER — `04_Sales_Invoices_List/code.html`.
 *
 * The reference's anatomy, in its order: an action cluster, a four-card KPI
 * strip, then one workspace card holding the status tabs, the search strip, the
 * dense grid and a footer saying how many rows this is.
 *
 * **THE TABS ARE A VIEW, NOT A SECOND FILTER.** Their counts are of the rows the
 * server already sent, and "All" hides none of them. Which invoices exist at all
 * was decided by the record predicate in SQL.
 *
 * **THE FIGURES OVER THE TABLE ARE NOT SUMS OF THE TABLE.** Outstanding and
 * overdue come from `/finance/receivables/ageing`, which is the server's own
 * ageing of the same authorised relation — a total over the rows listed would
 * quietly mean "of the ones on this page".
 */
export function InvoicesScreen({
  gateway,
  legalEntityId,
  canCreate,
  canSend = false,
  onOpen,
  onNew,
  onViewDocument,
  onSend,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId?: string | undefined;
  readonly canCreate: boolean;
  /** A courtesy. The send route and the server refuse the act again. */
  readonly canSend?: boolean;
  readonly onOpen: (invoiceId: string) => void;
  readonly onNew: () => void;
  readonly onViewDocument?: ((invoiceId: string) => void) | undefined;
  readonly onSend?: ((invoiceId: string) => void) | undefined;
}) {
  const { state, reload } = useAsync<{
    invoices: readonly Invoice[];
    names: ReadonlyMap<string, string>;
    ageing: readonly AgeingBucket[];
  }>(
    async () => ({
      invoices: await gateway.invoices(legalEntityId),
      // WHO THE INVOICE IS TO. An invoice carries a party id; the name is the
      // customer register's, read under the permission it already guards on. A
      // refusal costs the column, not the page.
      names: await gateway
        .customers(legalEntityId)
        .then((all) => new Map(all.map((one) => [one.partyId, one.displayName])))
        .catch(() => new Map<string, string>()),
      ageing: await gateway.receivablesAgeing().catch(() => []),
    }),
    [gateway, legalEntityId],
  );

  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');

  if (state.status === 'loading') return <LoadingState title="Loading invoices…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { invoices, names, ageing } = state.value;
  const currency = invoices[0]?.currency ?? 'GBP';

  const drafts = invoices.filter((invoice) => invoice.status === 'draft');
  const issued = invoices.filter((invoice) => invoice.status !== 'draft');
  const outstanding = sumMinor(ageing.map((bucket) => bucket.outstanding));
  const overdue = sumMinor(
    ageing.filter((bucket) => bucket.bucket !== 'current').map((bucket) => bucket.outstanding),
  );
  const debtors = new Set(ageing.map((bucket) => bucket.partyId)).size;

  /*
   * THE REFERENCE'S FOUR, FILLED WITH FIGURES THAT ARE TRUE OF THIS DATA.
   *
   * Stitch heads them "Total Invoiced (This Month)", "Outstanding Receivables",
   * "Overdue Amount" and "Draft Invoices Value". The month window is not a thing
   * this list carries — it arrives as the invoices within reach, newest first —
   * so the first card is labelled for what it actually totals rather than being
   * given a period it does not have. Four cards, one row; none invented.
   */
  const summary = (
    <SummaryBand>
      <SummaryCard
        icon="tray"
        tone="teal"
        label="Invoiced"
        value={moneyOf(sumMinor(issued.map((invoice) => invoice.grossTotal)), currency)}
        caption={`${String(issued.length)} issued`}
      />
      <SummaryCard
        icon="bank"
        tone="info"
        label="Outstanding receivables"
        value={moneyOf(outstanding, currency)}
        caption={`${String(debtors)} debtors`}
      />
      <SummaryCard
        icon="warning"
        urgent
        label="Overdue amount"
        value={moneyOf(overdue, currency)}
        caption="Past its due date"
      />
      <SummaryCard
        icon="calendar-blank"
        tone="neutral"
        label="Draft value (net)"
        value={moneyOf(sumMinor(drafts.map((invoice) => invoice.netTotal)), currency)}
        caption={`${String(drafts.length)} drafts · tax at issue`}
      />
    </SummaryBand>
  );

  const header = (
    <PageHeader
      actions={
        canCreate ? (
          <Button variant="primary" icon={<Icon name="plus" size={14} />} onClick={onNew}>
            New invoice
          </Button>
        ) : undefined
      }
    />
  );

  if (invoices.length === 0) {
    return (
      <>
        {header}
        {summary}
        <Card>
          <EmptyState
            icon="bank"
            title="No invoices"
            description="Invoices within your reach appear here."
          />
        </Card>
      </>
    );
  }

  const statuses = [...new Set(invoices.map((invoice) => invoice.status))].sort();
  const tabs = [
    { id: 'all', label: 'All', count: invoices.length },
    ...statuses.map((status) => ({
      id: status,
      label: status.charAt(0).toUpperCase() + status.slice(1),
      count: invoices.filter((invoice) => invoice.status === status).length,
    })),
  ];

  const needle = query.trim().toLowerCase();
  const shown = invoices.filter((invoice) => {
    if (tab !== 'all' && invoice.status !== tab) return false;
    if (needle === '') return true;
    const haystack = [
      invoice.invoiceNumber ?? '',
      names.get(invoice.partyId) ?? '',
      invoice.customerReference ?? '',
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });

  return (
    <>
      {header}
      {summary}
      <RecordsCard>
        <TabStrip label="Invoice status" tabs={tabs} active={tab} onSelect={setTab} />
        <FilterBar flush>
          <FilterSearch
            label="Search invoices"
            placeholder="Search by invoice number, customer or reference…"
            value={query}
            onChange={setQuery}
          />
          {/*
            NO EXPORT MENU AND NO BATCH ACTIONS. The reference draws both; there
            is no export of this list and no bulk operation behind either, and a
            menu that opens onto nothing is worse than no menu (§4E).
          */}
        </FilterBar>
        {/*
          SEVEN COLUMNS, WHICH IS STITCH'S OWN SET MINUS THE ONE WE HAVE NOT GOT.
          The reference draws a select box, invoice number, customer, issue date,
          due date, total, BALANCE DUE, status and actions. Nothing here selects
          rows in bulk, and an invoice carries no per-document outstanding — what
          remains owed is the allocations' answer, on the ageing. The net and the
          VAT were here for one pass and cost the status and action columns their
          place: at 1280 this card is 958 pixels and nine money-and-date columns
          are not. Both are on the invoice itself, which is where a reader asking
          "how much of that is tax" is looking.
        */}
        <RecordsScroll dense>
          <Table label="Sales invoices">
            <thead>
              <Tr>
                <Th>Invoice #</Th>
                <Th>Customer</Th>
                <Th>Issue date</Th>
                <Th>Due date</Th>
                <Th align="right">Total</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </Tr>
            </thead>
            <tbody>
              {shown.map((invoice) => {
                const name = names.get(invoice.partyId);
                const late =
                  invoice.dueDate === null ? null : daysBetween(invoice.dueDate, today());
                return (
                  <Tr key={invoice.id}>
                    <Td>
                      <EntityCell
                        name={invoice.invoiceNumber ?? 'Draft'}
                        {...(invoice.customerReference === null
                          ? {}
                          : { sub: invoice.customerReference })}
                        onOpen={() => {
                          onOpen(invoice.id);
                        }}
                      />
                    </Td>
                    <Td>
                      {name === undefined ? (
                        <FieldValue muted>Not in your customer list</FieldValue>
                      ) : (
                        <EntityCell mark={initialsOf(name)} name={name} />
                      )}
                    </Td>
                    <Td variant="secondary">{formatDay(invoice.issueDate)}</Td>
                    <Td variant="secondary">
                      {formatDay(invoice.dueDate)}
                      {/*
                        "14d overdue" is the reference's caption beside a late
                        due date. It is a count of days between two dates the
                        server sent, and it decides nothing: whether an invoice
                        is overdue is `status`, and this never contradicts it.
                      */}
                      {invoice.status === 'overdue' && late !== null && late > 0 && (
                        <RowDetail>{`${String(late)}d overdue`}</RowDetail>
                      )}
                    </Td>
                    <Td variant="money">{moneyOf(invoice.grossTotal, invoice.currency)}</Td>
                    <Td>
                      <StatusChip tone={statusTone(invoice.status)}>{invoice.status}</StatusChip>
                    </Td>
                    <Td>
                      <RowActions>
                        {onViewDocument !== undefined && (
                          <RowAction
                            icon="tray"
                            label={`View the document for ${invoice.invoiceNumber ?? 'this draft'}`}
                            onClick={() => {
                              onViewDocument(invoice.id);
                            }}
                          />
                        )}
                        {canSend && onSend !== undefined && invoice.invoiceNumber !== null && (
                          <RowAction
                            icon="arrow-square-out"
                            label={`Send ${invoice.invoiceNumber} to the customer`}
                            onClick={() => {
                              onSend(invoice.id);
                            }}
                          />
                        )}
                      </RowActions>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </RecordsScroll>
        <RecordsFooter>
          <span>
            Showing <RecordsCount>{shown.length}</RecordsCount> of{' '}
            <RecordsCount>{invoices.length}</RecordsCount> invoices within your reach
          </span>
          {/*
            NO PAGE BUTTONS. The reference draws 1 · 2 · 3 · … · 15 and a page
            size; `/finance/invoices` takes a limit and no offset, so numbered
            pages would be buttons that cannot page.
          */}
          <span>Newest issue date first</span>
        </RecordsFooter>
      </RecordsCard>
    </>
  );
}

// ---- 2. Screen 06 — the invoice ---------------------------------------------

/**
 * ONE INVOICE — `06_Sales_Invoice_Detail/code.html`.
 *
 * The reference's anatomy: a status header carrying the record's own name, a
 * lifecycle stepper, then an eight/four split with the document on the left and
 * the operational rail — collection status, the ledger journal, the dispatch
 * history — on the right.
 *
 * **THE STEPPER SHOWS THE LIFECYCLE THIS PRODUCT HAS.** Stitch draws Draft →
 * Approved → Sent → Viewed → Paid. There is no approval step on a sales invoice
 * and nothing observes a customer opening one, so those two are absent rather
 * than drawn as steps that never light (§4E).
 *
 * **THE DOCUMENT PANEL IS THE RECORD, NOT A SECOND RENDERER.** It shows the same
 * stored figures the PDF is built from and says so; the PDF itself is one click
 * away and remains the only thing the customer receives.
 */
export function InvoiceDetailScreen({
  gateway,
  invoiceId,
  canIssue,
  canEdit = false,
  canDispute = false,
  canSend = false,
  onViewDocument,
  onSend,
  onIssued,
}: {
  readonly gateway: FinanceGateway;
  readonly invoiceId: string;
  readonly canIssue: boolean;
  /**
   * Whether to OFFER the draft editor. `finance.invoice.create` is what the
   * API requires to replace lines, and this is the courtesy of not showing a
   * button that will be refused — not the decision. The server refuses an
   * issued invoice's lines with 409 whatever this says, which is the control.
   */
  readonly canEdit?: boolean;
  /**
   * Whether to OFFER the hold and release controls. `finance.invoice.dispute`
   * is a permission of its own, and somebody who may issue an invoice is not
   * by that fact allowed to stop it being chased.
   */
  readonly canDispute?: boolean;
  /**
   * WHETHER TO OFFER THE DOCUMENT AND THE SEND, and they are separate for a
   * reason. Viewing the PDF is a read of the invoice — anybody who can open this
   * page can have it. Sending is `finance.invoice.send`, the only act in Finance
   * that puts the tenant's name in front of an outside party.
   */
  readonly canSend?: boolean;
  readonly onViewDocument?: (() => void) | undefined;
  readonly onSend?: (() => void) | undefined;
  readonly onIssued?: (invoice: Invoice) => void;
}) {
  const { state, reload } = useAsync<{
    invoice: Invoice;
    lines: readonly InvoiceLine[];
    customer: FinanceCustomer | null;
    terms: readonly FinancePaymentTerm[];
    accounts: readonly Account[];
    journalLines: readonly { accountId: string; debit: string; credit: string }[];

    journalNumber: string | null;
  }>(async () => {
    const { invoice, lines } = await gateway.invoice(invoiceId);
    const customers = await gateway.customers(invoice.legalEntityId).catch(() => []);
    const terms = await gateway.paymentTerms(invoice.legalEntityId).catch(() => []);
    const accounts = await gateway.accounts(invoice.legalEntityId).catch(() => []);
    /*
     * THE POSTING, READ FROM THE LEDGER RATHER THAN RE-DERIVED. The reference
     * draws a three-line journal beside the invoice; this is that journal, as
     * the posting engine wrote it, under the permission the ledger guards on.
     */
    const journal =
      invoice.journalId === null
        ? null
        : await gateway.journal(invoice.journalId).catch(() => null);
    return {
      invoice,
      lines,
      customer: customers.find((entry) => entry.partyId === invoice.partyId) ?? null,
      terms,
      accounts,
      /*
       * THE POSTING IN THE JOURNAL'S OWN CURRENCY. `…Txn` is what the document
       * was raised in and `…Base` is the entity's; an invoice screen shows the
       * invoice's currency, which is the transaction one. They differ the
       * moment a tenant bills in something other than its base currency, so
       * which pair is being read is stated rather than assumed.
       */
      journalLines:
        journal?.lines.map((line) => ({
          accountId: line.accountId,
          debit: line.debitTxn,
          credit: line.creditTxn,
        })) ?? [],
      journalNumber: journal?.journal.journalNumber ?? null,
    };
  }, [gateway, invoiceId]);

  const [issuing, setIssuing] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading invoice…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { invoice, lines, customer, terms, accounts, journalLines, journalNumber } = state.value;
  const isDraft = invoice.status === 'draft';
  const term = terms.find((entry) => entry.id === customer?.paymentTermId);
  const accountName = (id: string) => {
    const account = accounts.find((entry) => entry.id === id);
    return account === undefined ? id : `${account.code} · ${account.name}`;
  };
  const late = invoice.dueDate === null ? null : daysBetween(today(), invoice.dueDate);

  async function issue() {
    setIssuing(true);
    setProblem(undefined);
    try {
      const issued = await gateway.issueInvoice(invoiceId, {});
      onIssued?.(issued);
      reload();
    } catch (error) {
      // THE SERVER'S WORDS. A closed period, a customer on hold and a missing
      // control account are all refusals with a sentence the operator can act
      // on, and inventing our own would lose the sentence.
      setProblem(refusalText(error, 'The invoice could not be issued.'));
    } finally {
      setIssuing(false);
    }
  }

  /*
   * THE LIFECYCLE THE LEDGER ACTUALLY RECORDS. "Issued" is the tax point and
   * carries the issue date; "Settled" is the server's own status, never a
   * comparison made here.
   */
  const steps: readonly LifecycleStep[] = [
    { label: 'Draft', state: 'done' },
    {
      label: 'Issued',
      state: isDraft ? 'pending' : 'done',
      ...(invoice.issueDate === null ? {} : { when: formatDay(invoice.issueDate) }),
    },
    {
      label: 'Posted',
      state: invoice.journalId === null ? 'pending' : 'done',
      ...(journalNumber === null ? {} : { when: journalNumber }),
    },
    {
      label: 'Settled',
      state: invoice.status === 'paid' ? 'done' : isDraft ? 'pending' : 'current',
      ...(invoice.dueDate === null ? {} : { when: `due ${formatDay(invoice.dueDate)}` }),
    },
  ];

  return (
    <>
      <PageHeader
        actions={
          <>
            {/*
              THE DOCUMENT IS OFFERED FOR A DRAFT TOO, watermarked, because
              seeing what an invoice will look like before it becomes a statutory
              record is the point of a preview. The SEND is not: a draft has no
              number for a customer to pay against.
            */}
            {onViewDocument !== undefined && (
              <Button
                variant="secondary"
                icon={<Icon name="tray" size={14} />}
                onClick={onViewDocument}
              >
                View PDF
              </Button>
            )}
            {canSend && !isDraft && invoice.invoiceNumber !== null && onSend !== undefined && (
              <Button
                variant="secondary"
                icon={<Icon name="arrow-square-out" size={14} />}
                onClick={onSend}
              >
                Send to customer
              </Button>
            )}
            {canIssue && isDraft && (
              <Button
                variant="primary"
                onClick={() => {
                  void issue();
                }}
                disabled={issuing}
              >
                {issuing ? 'Issuing…' : 'Issue and post'}
              </Button>
            )}
          </>
        }
      />

      <IdentityCard
        name={invoice.invoiceNumber ?? 'Draft invoice'}
        {...(invoice.customerReference === null ? {} : { code: invoice.customerReference })}
        chips={<StatusChip tone={statusTone(invoice.status)}>{invoice.status}</StatusChip>}
        meta={
          <>
            <span>Customer</span>
            <MetaStrong>{customer?.displayName ?? 'Outside your customer list'}</MetaStrong>
            <MetaSeparator />
            <span>Total</span>
            <MetaStrong>{moneyOf(invoice.grossTotal, invoice.currency)}</MetaStrong>
            {invoice.dueDate !== null && (
              <>
                <MetaSeparator />
                <MetaStrong>
                  {late !== null && late < 0
                    ? `${String(Math.abs(late))} days past due`
                    : late !== null
                      ? `Due in ${String(late)} days`
                      : `Due ${formatDay(invoice.dueDate)}`}
                </MetaStrong>
              </>
            )}
          </>
        }
      />

      {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
      {invoice.reverseCharge !== null ? (
        /*
         * FR-VAT-008. THE SERVER'S SENTENCE, RENDERED VERBATIM. The wording is a
         * statutory requirement and the browser is not the place to compose it —
         * nor to work out the figure, which is the VAT the customer accounts for
         * and is deliberately not part of the total they pay us.
         */
        <Alert tone="info" title={invoice.reverseCharge.notice}>
          VAT of {moneyOf(invoice.reverseCharge.tax, invoice.currency)} is due from the customer
          under the reverse charge. It is not included in the invoice total.
        </Alert>
      ) : null}
      {invoice.disputeReason !== null ? (
        <Alert tone="warning" title="Held out of collections">
          {invoice.disputeReason} · The debt is unchanged and still reported; it is simply not being
          chased until the hold is resolved.
        </Alert>
      ) : null}
      {isDraft ? (
        <Alert tone="info" title="Tax is computed when the invoice is issued">
          A draft has no tax point, so it carries no tax. The rate in force on the issue date is
          applied by the server at that moment.
        </Alert>
      ) : null}

      <Panel>
        <Lifecycle steps={steps} />
      </Panel>

      <Split>
        <SplitMain span={8}>
          {/* THE DOCUMENT, as the reference lays it out: who from, who to, the
              lines, and the totals block under them. */}
          <Panel>
            <PanelHead title="Tax invoice" icon="bank">
              <PanelTag>{invoice.invoiceNumber ?? 'Not numbered until issued'}</PanelTag>
            </PanelHead>
            <FieldGrid columns={4}>
              <Field label="Issue date">
                <FieldValue strong>{formatDay(invoice.issueDate)}</FieldValue>
              </Field>
              <Field label="Payment terms">
                <FieldValue strong>{term?.name ?? 'Entity default'}</FieldValue>
              </Field>
              <Field label="Due date">
                <FieldValue strong>{formatDay(invoice.dueDate)}</FieldValue>
              </Field>
              <Field label="Reference">
                <FieldValue strong>{invoice.customerReference ?? '—'}</FieldValue>
              </Field>
            </FieldGrid>

            {/*
              FIVE COLUMNS, AND THE LEDGER CODING ON THE LINE'S SECOND ROW.
              This panel is eight of twelve columns — about 790 pixels at 1280 —
              and seven columns of figures do not fit in it; two of them were
              being pushed off the card's right-hand edge, which the shell's
              hidden scrollbars made invisible rather than merely tight. The
              reference itself puts a line's secondary detail under its
              description, so the account and the tax code go there: nothing is
              lost, and the amounts a reader came for all fit.
              The line number is gone for the same reason — the lines are in
              their stored order, which is all the number said.
            */}
            <RecordsScroll dense>
              <Table label="Invoice lines">
                <thead>
                  <Tr>
                    <Th>Item / description</Th>
                    <Th align="right">Qty</Th>
                    <Th align="right">Unit price</Th>
                    <Th align="right">Net</Th>
                    <Th align="right">VAT</Th>
                  </Tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <Tr key={line.id}>
                      <Td>
                        {line.description}
                        <RowDetail>
                          <CodeChip>{accountName(line.accountId)}</CodeChip>{' '}
                          {line.taxCode ?? 'no tax code'}
                        </RowDetail>
                      </Td>
                      <Td variant="money">{line.quantity}</Td>
                      <Td variant="money">{moneyOf(line.unitPrice, invoice.currency)}</Td>
                      <Td variant="money">{moneyOf(line.netAmount, invoice.currency)}</Td>
                      <Td variant="money">{moneyOf(line.taxAmount, invoice.currency)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </RecordsScroll>

            <Totals>
              <TotalsRow label="Subtotal" amount={moneyOf(invoice.netTotal, invoice.currency)} />
              <TotalsRow
                label="Value added tax"
                sublabel={isDraft ? 'Computed at issue' : 'Output VAT'}
                amount={moneyOf(invoice.taxTotal, invoice.currency)}
              />
              <TotalsRow
                label="Total"
                emphasis
                amount={moneyOf(invoice.grossTotal, invoice.currency)}
              />
            </Totals>
            <PanelNote icon="info">
              These are the stored figures the PDF is rendered from. The PDF is what the customer
              receives, and it is the only rendering of this invoice that leaves the building.
            </PanelNote>
          </Panel>

          {canEdit && isDraft ? (
            <InvoiceLineEditor
              gateway={gateway}
              invoiceId={invoiceId}
              legalEntityId={invoice.legalEntityId}
              lines={lines}
              onSaved={reload}
            />
          ) : null}
        </SplitMain>

        <SplitSide span={4}>
          <Panel>
            <PanelHead title="Collection status" icon="bank">
              <StatusChip tone={statusTone(invoice.status)}>{invoice.status}</StatusChip>
            </PanelHead>
            <TotalsBanner
              label={`Total payable (${invoice.currency})`}
              amount={moneyOf(invoice.grossTotal, invoice.currency)}
              side={
                <>
                  <PanelTag>{term?.name ?? 'Entity default'}</PanelTag>
                  <span>{formatDay(invoice.dueDate)}</span>
                </>
              }
            />
            <PanelNote icon="info">
              {isDraft
                ? 'Nothing is owed yet — a draft has no number for a customer to pay against.'
                : 'What remains owed is whatever the allocations say; receipts are applied on the receipts screen.'}
            </PanelNote>
          </Panel>

          {/*
            THE LEDGER POSTING. The reference draws a mini general-ledger table
            beside the invoice; this is the journal the posting engine actually
            wrote, account by account.
          */}
          <Panel>
            <PanelHead title="General ledger journal" icon="chart-bar">
              {invoice.journalId === null ? (
                <StatusChip tone="neutral">Not posted</StatusChip>
              ) : (
                <StatusChip tone="success">Posted</StatusChip>
              )}
            </PanelHead>
            {invoice.journalId === null ? (
              <PanelNote icon="info">
                A draft is not in the books. The journal appears when the invoice is issued.
              </PanelNote>
            ) : journalLines.length === 0 ? (
              <PanelNote icon="lock-simple">
                This invoice is posted as journal {journalNumber ?? invoice.journalId}, and its
                lines are outside your access.
              </PanelNote>
            ) : (
              <>
                <PanelFoot>
                  <span>Journal voucher</span>
                  <PanelTag>{journalNumber ?? invoice.journalId}</PanelTag>
                </PanelFoot>
                <Totals>
                  {journalLines.map((line, index) => (
                    <TotalsRow
                      key={`${line.accountId}:${String(index)}`}
                      label={accountName(line.accountId)}
                      sublabel={line.debit === '0' ? 'Credit' : 'Debit'}
                      amount={moneyOf(
                        line.debit === '0' ? line.credit : line.debit,
                        invoice.currency,
                      )}
                    />
                  ))}
                </Totals>
              </>
            )}
          </Panel>

          {isDraft ? null : (
            <InvoiceDisputePanel
              gateway={gateway}
              invoiceId={invoiceId}
              invoice={invoice}
              canDispute={canDispute}
              onChanged={reload}
            />
          )}

          {/*
            THE DELIVERY HISTORY, ON THE INVOICE. Screen 33 is the entity-wide
            register; this is the same question asked about one document, and it
            is here because "did the customer get it?" is asked while looking at
            the invoice, not while looking at a register.
          */}
          {isDraft ? null : <InvoiceDeliveriesPanel gateway={gateway} invoiceId={invoiceId} />}
        </SplitSide>
      </Split>
    </>
  );
}

/**
 * HOLDING AN INVOICE OUT OF COLLECTIONS, AND PUTTING IT BACK (FR-ARC-003).
 *
 * **THIS PANEL CHANGES NO ACCOUNTING FACT AND OFFERS NO WAY TO.** There is no
 * amount field, because reducing what is owed is a credit note — a different
 * act, a different permission and a posting of its own. All this sends is a
 * sentence explaining why the invoice is not being chased.
 *
 * **THE HISTORY IS SHOWN, NOT JUST THE CURRENT HOLD.** An invoice can be
 * disputed, resolved and disputed again for a different reason, and a credit
 * controller deciding what to do next needs the sequence.
 */
function InvoiceDisputePanel({
  gateway,
  invoiceId,
  invoice,
  canDispute,
  onChanged,
}: {
  readonly gateway: FinanceGateway;
  readonly invoiceId: string;
  readonly invoice: Invoice;
  readonly canDispute: boolean;
  readonly onChanged: () => void;
}) {
  const { state, reload } = useAsync<readonly InvoiceDispute[]>(
    () => gateway.invoiceDisputes(invoiceId),
    [gateway, invoiceId, invoice.version],
  );
  const [reason, setReason] = useState('');
  const [resolution, setResolution] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  const held = invoice.status === 'disputed';

  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    setProblem(undefined);
    try {
      await work();
      setReason('');
      setResolution('');
      reload();
      onChanged();
    } catch (error) {
      // The server's sentence, verbatim: "only an issued or part-paid invoice
      // can be held" is a better message than anything invented here.
      setProblem(refusalText(error, 'The hold could not be changed.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel>
      <PanelHead title="Collections" icon="warning">
        {held ? (
          <StatusChip tone={statusTone('disputed')}>Disputed</StatusChip>
        ) : (
          <StatusChip tone="neutral">Being chased</StatusChip>
        )}
      </PanelHead>
      {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}

      {canDispute && !held ? (
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            void run(() => gateway.disputeInvoice(invoiceId, { reason }));
          }}
        >
          <FormSection title="Hold this invoice">
            <TextField
              label="Reason"
              name="disputeReason"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
              helper="Recorded against the invoice, with who raised it and when. No amount changes."
              required
            />
          </FormSection>
          <FormActions submitLabel="Hold out of collections" submitting={busy} />
        </Form>
      ) : null}

      {canDispute && held ? (
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            void run(() => gateway.resolveInvoiceDispute(invoiceId, { resolution }));
          }}
        >
          <FormSection title="Resolve the hold">
            <TextField
              label="How it was resolved"
              name="disputeResolution"
              value={resolution}
              onChange={(event) => {
                setResolution(event.target.value);
              }}
              helper="The invoice returns to whatever its allocations say it is — the server decides that, not this form."
              required
            />
          </FormSection>
          <FormActions submitLabel="Return to collections" submitting={busy} />
        </Form>
      ) : null}

      {state.status === 'failed' ? <Refusal error={state.error} onRetry={reload} /> : null}
      {state.status === 'ready' && state.value.length === 0 ? (
        <PanelNote icon="check">No hold has ever been placed on this invoice.</PanelNote>
      ) : null}
      {state.status === 'ready' && state.value.length > 0 ? (
        <Timeline>
          {state.value.map((dispute) => (
            <TimelineEvent
              key={dispute.id}
              tone={dispute.resolvedAt === null ? 'warning' : 'success'}
              when={formatDay(dispute.raisedAt.slice(0, 10))}
              title={dispute.reason}
            >
              {dispute.resolvedAt === null
                ? 'Still open.'
                : `Resolved ${formatDay(dispute.resolvedAt.slice(0, 10))} — ${dispute.resolution ?? 'no note'}`}
            </TimelineEvent>
          ))}
        </Timeline>
      ) : null}
    </Panel>
  );
}

/**
 * EDITING AN ISSUED-DRAFT'S LINES FROM THE RECORD PAGE.
 *
 * REPLACE, NOT PATCH. `PUT /finance/invoices/{id}/lines` takes the whole set,
 * because a line-by-line edit of a document whose totals and tax are recomputed
 * server-side would invite a client to send half a change.
 *
 * NOTHING HERE DECIDES WHETHER IT IS ALLOWED. An issued invoice's lines are
 * refused with 409 by the server, which is the control; this panel simply does
 * not offer the form for one, and renders the refusal verbatim if it arrives
 * anyway — which it will if somebody else issues the invoice mid-edit.
 */
function InvoiceLineEditor({
  gateway,
  invoiceId,
  legalEntityId,
  lines,
  onSaved,
}: {
  readonly gateway: FinanceGateway;
  readonly invoiceId: string;
  readonly legalEntityId: string;
  readonly lines: readonly InvoiceLine[];
  readonly onSaved: () => void;
}) {
  const { state, reload } = useAsync<readonly Account[]>(
    () => gateway.accounts(legalEntityId),
    [gateway, legalEntityId],
  );

  const [draft, setDraft] = useState<readonly EditorLine[]>(() =>
    lines.length === 0 ? [blankLine(0)] : lines.map(editorLineFrom),
  );
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading accounts…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const income = state.value.filter((account) => account.accountType === 'income');

  async function save() {
    setSaving(true);
    setProblem(undefined);
    try {
      await gateway.replaceInvoiceLines(invoiceId, draft.map(toDraftLine));
      onSaved();
    } catch (error) {
      setProblem(refusalText(error, 'The lines could not be changed.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <RecordsCard>
      <RecordsToolbar
        icon="list"
        title="Edit the draft's lines"
        count={`${String(draft.length)} ${draft.length === 1 ? 'item' : 'items'}`}
        actions={
          <Button
            variant="primary"
            disabled={saving}
            onClick={() => {
              void save();
            }}
          >
            {saving ? 'Saving…' : 'Save the lines'}
          </Button>
        }
      />
      {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
      <LineGrid accounts={income} lines={draft} onChange={setDraft} />
      <Ribbon note="Saving replaces the whole set. Totals and tax are recomputed by the server.">
        <Button
          variant="secondary"
          icon={<Icon name="plus" size={14} />}
          onClick={() => {
            setDraft([...draft, blankLine(draft.length)]);
          }}
        >
          Add line item
        </Button>
      </Ribbon>
    </RecordsCard>
  );
}

// ---- 3. Screen 05 — raising an invoice --------------------------------------

/**
 * One editable line, held in the browser until the server is asked to store it.
 *
 * `unitPrice` IS MINOR UNITS, as the API takes it, and the field says so. It is
 * carried as the string the operator typed and handed to the server unchanged —
 * nothing here parses it into a number, because a number is where the precision
 * goes.
 */
interface EditorLine {
  readonly key: string;
  readonly description: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly taxCode: string;
  readonly accountId: string;
}

let lineCounter = 0;

function blankLine(index: number): EditorLine {
  lineCounter += 1;
  return {
    key: `line-${String(index)}-${String(lineCounter)}`,
    description: '',
    quantity: '1',
    unitPrice: '',
    taxCode: 'GB_STD',
    accountId: '',
  };
}

function editorLineFrom(line: InvoiceLine): EditorLine {
  return {
    key: line.id,
    description: line.description,
    quantity: line.quantity,
    unitPrice: line.unitPrice,
    taxCode: line.taxCode ?? 'GB_STD',
    accountId: line.accountId,
  };
}

function toDraftLine(line: EditorLine): DraftLine {
  return {
    description: line.description,
    quantity: line.quantity,
    unitPrice: line.unitPrice,
    accountId: line.accountId,
    ...(line.taxCode === '' ? {} : { taxCode: line.taxCode }),
  };
}

/** The four codes the shared tax kernel recognises for UK sales. */
const TAX_CODES: readonly { readonly value: string; readonly label: string }[] = [
  { value: 'GB_STD', label: 'Standard' },
  { value: 'GB_RED', label: 'Reduced' },
  { value: 'GB_ZER', label: 'Zero' },
  { value: 'GB_EXM', label: 'Exempt' },
];

/**
 * THE LINE GRID — `05_New_Sales_Invoice/code.html`, the middle card.
 *
 * Stitch draws nine columns: `#`, item, account code, quantity, unit price,
 * `Disc %`, VAT rate, line total and a delete button. TWO OF THOSE ARE NOT
 * COLUMNS THIS PRODUCT HAS:
 *
 *   `Disc %`     an invoice line has no discount field. A per-line percentage
 *                that silently did nothing would be the worst kind of control,
 *                so it is absent; a discount is a line of its own.
 *   `Line Total` a draft's line net is computed at the TAX POINT by the server
 *                and does not exist until the draft is stored. The column is
 *                kept and shows the server's figure once there is one; before
 *                that it shows a dash, because quantity × price done here would
 *                be the browser holding an opinion about money.
 */
function LineGrid({
  accounts,
  lines,
  stored,
  currency = 'GBP',
  onChange,
}: {
  readonly accounts: readonly Account[];
  readonly lines: readonly EditorLine[];
  /** The server's own line figures, once the draft has been stored. */
  readonly stored?: readonly InvoiceLine[] | undefined;
  readonly currency?: string;
  readonly onChange: (lines: readonly EditorLine[]) => void;
}) {
  function update(index: number, patch: Partial<EditorLine>) {
    onChange(lines.map((line, at) => (at === index ? { ...line, ...patch } : line)));
  }

  return (
    <RecordsScroll>
      <Table label="Line items and ledger distribution">
        <thead>
          <Tr>
            <Th>#</Th>
            <Th>Item / description</Th>
            <Th>Account code</Th>
            <Th align="right">Quantity</Th>
            <Th align="right">Unit price (minor units)</Th>
            <Th>VAT rate</Th>
            <Th align="right">Line net</Th>
            <Th align="right">Remove</Th>
          </Tr>
        </thead>
        <tbody>
          {lines.map((line, index) => {
            const server = stored?.[index];
            return (
              <Tr key={line.key}>
                <Td variant="secondary">{String(index + 1).padStart(2, '0')}</Td>
                <Td>
                  <CellInput
                    label={`Description of line ${String(index + 1)}`}
                    value={line.description}
                    placeholder="What is being billed"
                    onChange={(value) => {
                      update(index, { description: value });
                    }}
                  />
                </Td>
                <Td>
                  <CellSelect
                    label={`Income account for line ${String(index + 1)}`}
                    value={line.accountId}
                    placeholder="Choose an account"
                    options={accounts.map((account) => ({
                      value: account.id,
                      label: `${account.code} · ${account.name}`,
                    }))}
                    onChange={(value) => {
                      update(index, { accountId: value });
                    }}
                  />
                </Td>
                <Td>
                  <CellInput
                    label={`Quantity on line ${String(index + 1)}`}
                    numeric
                    value={line.quantity}
                    onChange={(value) => {
                      update(index, { quantity: value });
                    }}
                  />
                </Td>
                <Td>
                  <CellInput
                    label={`Unit price on line ${String(index + 1)}, in minor units`}
                    numeric
                    value={line.unitPrice}
                    placeholder="100000"
                    onChange={(value) => {
                      update(index, { unitPrice: value });
                    }}
                  />
                </Td>
                <Td>
                  <CellSelect
                    label={`Tax code on line ${String(index + 1)}`}
                    value={line.taxCode}
                    options={TAX_CODES}
                    onChange={(value) => {
                      update(index, { taxCode: value });
                    }}
                  />
                </Td>
                <Td variant="money">
                  {server === undefined ? '—' : moneyOf(server.netAmount, currency)}
                </Td>
                <Td>
                  <RowActions>
                    <RowAction
                      icon="x"
                      danger
                      label={`Remove line ${String(index + 1)}`}
                      disabled={lines.length === 1}
                      onClick={() => {
                        onChange(lines.filter((_, at) => at !== index));
                      }}
                    />
                  </RowActions>
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
    </RecordsScroll>
  );
}

/**
 * RAISING A DRAFT — `05_New_Sales_Invoice/code.html`, converted whole.
 *
 * The reference's anatomy, in its order: a command header of actions; a
 * seven/five split holding the customer block and the invoice parameters; the
 * line-item grid with its add ribbon; then a seven/five split holding the
 * remittance details on the left and the financial calculation on the right.
 *
 * **THE PAGE HAS TWO PHASES AND THE REFERENCE SHOWS BOTH.** Stitch's header
 * reads "Draft saved 12s ago", which is a screen you stay on after saving. So
 * does this one: "Save as draft" stores the draft and the same page continues
 * with a number, a status and — the point — the server's own totals. Before that
 * the totals are dashes, because a total nobody has computed is not zero.
 *
 * **THE CHOICES COME FROM THE SERVER.** The customer list and the account list
 * are both fetched and both already filtered by the caller's own predicate, so a
 * person who reaches one legal entity is offered that entity's customers and no
 * others.
 *
 * **THE DUE DATE IS THE CUSTOMER'S TERM, AND THIS SCREEN DOES NOT SET IT.**
 * `POST /finance/invoices/{id}/issue` dates the invoice from
 * `finance_customer.payment_term_id` when no `dueInDays` is sent, and no
 * `dueInDays` is sent. The term is shown so the operator knows what will happen.
 *
 * **OMITTED FROM THE REFERENCE (§4E).** VIES validation, a payment profile, a
 * ledger period chip, automatic late interest, a column chooser, timesheet
 * import, discount and milestone lines, a per-invoice customer note, audit
 * attachments, an early-settlement discount, a controller sign-off and a
 * compliance check. Not one of them exists behind an API.
 */
export function InvoiceEditorScreen({
  gateway,
  parties,
  legalEntityId,
  canAddCustomer,
  canIssue = false,
  onCreated,
  onCancel,
  onEditCustomer,
  onViewDocument,
}: {
  readonly gateway: FinanceGateway;
  readonly parties: PartiesGateway;
  readonly legalEntityId: string;
  /** A courtesy. The server refuses the two writes again if it is wrong. */
  readonly canAddCustomer: boolean;
  /** Whether to offer "Issue invoice" beside "Save as draft". */
  readonly canIssue?: boolean;
  readonly onCreated: (invoice: Invoice) => void;
  readonly onCancel?: (() => void) | undefined;
  readonly onEditCustomer?: ((partyId: string) => void) | undefined;
  readonly onViewDocument?: ((invoiceId: string) => void) | undefined;
}) {
  const { state, reload } = useAsync<{
    customers: readonly FinanceCustomer[];
    accounts: readonly Account[];
    terms: readonly FinancePaymentTerm[];
    settings: DocumentSettings | null;
  }>(
    async () => ({
      customers: await gateway.customers(legalEntityId),
      accounts: await gateway.accounts(legalEntityId),
      terms: await gateway.paymentTerms(legalEntityId).catch(() => []),
      // What this entity prints on the document. A refusal costs the remittance
      // panel, not the page — and an entity with none configured is TOLD so,
      // because a document sent without bank details is the thing that costs.
      settings: await gateway.documentSettings(legalEntityId).catch(() => null),
    }),
    [gateway, legalEntityId],
  );

  const [partyId, setPartyId] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  /**
   * THE LIST AFTER A CUSTOMER WAS ADDED — RE-READ, NOT APPENDED TO.
   *
   * `reload()` would put the whole screen back into its loading state and take
   * the half-typed invoice with it, which is precisely the interruption this
   * panel exists to remove. What it is NOT is the new record pushed onto the
   * array in the browser: that would be the browser asserting the write
   * succeeded and asserting what the server stored.
   */
  const [reread, setReread] = useState<readonly FinanceCustomer[] | undefined>(undefined);
  const [reference, setReference] = useState('');
  const [issueDate, setIssueDate] = useState(today());
  const [lines, setLines] = useState<readonly EditorLine[]>(() => [blankLine(0)]);
  /** The draft once the server has stored it, with the figures it computed. */
  const [saved, setSaved] = useState<
    { invoice: Invoice; lines: readonly InvoiceLine[] } | undefined
  >(undefined);
  const [busy, setBusy] = useState<'idle' | 'saving' | 'issuing'>('idle');
  const [problem, setProblem] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { accounts, terms, settings } = state.value;
  const customers = reread ?? state.value.customers;
  const income = accounts.filter((account) => account.accountType === 'income');
  const customer = customers.find((entry) => entry.partyId === partyId);
  const term = terms.find((entry) => entry.id === customer?.paymentTermId);
  const currency = saved?.invoice.currency ?? 'GBP';

  /** Store the draft, or replace the lines of the one already stored. */
  async function store(): Promise<Invoice> {
    if (saved === undefined) {
      const invoice = await gateway.createInvoice({
        legalEntityId,
        partyId,
        currency: 'GBP',
        ...(reference === '' ? {} : { customerReference: reference }),
        lines: lines.map(toDraftLine),
      });
      const full = await gateway.invoice(invoice.id);
      setSaved(full);
      setLines(full.lines.map(editorLineFrom));
      return full.invoice;
    }
    await gateway.replaceInvoiceLines(saved.invoice.id, lines.map(toDraftLine));
    const full = await gateway.invoice(saved.invoice.id);
    setSaved(full);
    setLines(full.lines.map(editorLineFrom));
    return full.invoice;
  }

  async function saveDraft() {
    setBusy('saving');
    setProblem(undefined);
    try {
      await store();
    } catch (error) {
      setProblem(refusalText(error, 'The draft could not be saved.'));
    } finally {
      setBusy('idle');
    }
  }

  async function issue() {
    setBusy('issuing');
    setProblem(undefined);
    try {
      const draft = await store();
      /*
       * NO `dueInDays`. The server dates the invoice from the customer's payment
       * term; sending a day count here would override a term somebody set on
       * purpose. `issueDate` is passed because the operator chose it.
       */
      const issued = await gateway.issueInvoice(draft.id, { issueDate });
      onCreated(issued);
    } catch (error) {
      setProblem(refusalText(error, 'The invoice could not be issued.'));
    } finally {
      setBusy('idle');
    }
  }

  const stored = saved?.invoice;
  const dash = '—';

  return (
    <>
      {/*
        THE COMMAND HEADER'S ACTIONS. Its other half — the breadcrumb and the
        `<h1>` "New Sales Invoice" — belongs to the shell, which draws both from
        the route table.
      */}
      <PageHeader
        actions={
          <>
            {onCancel !== undefined && (
              <Button variant="secondary" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button
              variant="secondary"
              disabled={busy !== 'idle' || partyId === ''}
              onClick={() => {
                void saveDraft();
              }}
            >
              {busy === 'saving' ? 'Saving…' : saved === undefined ? 'Save as draft' : 'Save lines'}
            </Button>
            {/*
              PREVIEW PDF IS REAL, AND ONLY ONCE THERE IS SOMETHING TO RENDER.
              The renderer watermarks a draft precisely so this button can exist;
              before the draft is stored there is no document to ask for, so the
              control says why rather than failing when pressed.
            */}
            {onViewDocument !== undefined && (
              <Button
                variant="secondary"
                disabled={stored === undefined}
                title={
                  stored === undefined
                    ? 'Save the draft first — the document is rendered from what is stored.'
                    : undefined
                }
                onClick={() => {
                  if (stored !== undefined) onViewDocument(stored.id);
                }}
              >
                Preview PDF
              </Button>
            )}
            {canIssue && (
              <Button
                variant="primary"
                disabled={busy !== 'idle' || partyId === ''}
                onClick={() => {
                  void issue();
                }}
              >
                {busy === 'issuing' ? 'Issuing…' : 'Issue invoice'}
              </Button>
            )}
          </>
        }
      />

      {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}

      <Split>
        {/* ---- the customer block, `lg:col-span-7` ------------------------- */}
        <SplitMain span={7}>
          <Panel>
            <PanelHead label="Customer (debtor) entity">
              {canAddCustomer && (
                <Button
                  variant="tertiary"
                  icon={<Icon name="plus" size={13} />}
                  onClick={() => {
                    setAddOpen(true);
                  }}
                >
                  Add a customer
                </Button>
              )}
            </PanelHead>
            <SelectShell
              label="Customer"
              icon="users-three"
              value={partyId}
              placeholder="Choose a customer"
              options={customers.map((entry) => ({
                value: entry.partyId,
                label: entry.isOnHold ? `${entry.displayName} (on credit hold)` : entry.displayName,
              }))}
              onChange={setPartyId}
            />
            <Panel variant="inset">
              <FieldGrid columns={3}>
                <Field label="Billing email">
                  <FieldValue muted={customer?.billingEmail == null}>
                    {customer?.billingEmail ?? 'Not recorded — a send will ask for an address'}
                  </FieldValue>
                </Field>
                <Field label="Credit &amp; terms">
                  <FieldValue strong>{term?.name ?? 'Entity default'}</FieldValue>
                  <FieldValue muted>
                    {customer?.creditLimit == null
                      ? 'No credit limit set'
                      : `Credit limit ${moneyOf(customer.creditLimit, customer.billingCurrency ?? 'GBP')}`}
                  </FieldValue>
                </Field>
                <Field label="Standing">
                  <FieldValue>
                    {customer === undefined ? (
                      dash
                    ) : customer.isOnHold ? (
                      <StatusChip tone="warning">On credit hold</StatusChip>
                    ) : customer.isActive ? (
                      <StatusChip tone="success">Active</StatusChip>
                    ) : (
                      <StatusChip tone="neutral">Inactive</StatusChip>
                    )}
                  </FieldValue>
                </Field>
              </FieldGrid>
            </Panel>
            <PanelFoot>
              <PanelNote icon="info">
                The address and registrations printed on the document come from the shared party
                record.
              </PanelNote>
              {onEditCustomer !== undefined && customer !== undefined && (
                <Button
                  variant="tertiary"
                  onClick={() => {
                    onEditCustomer(customer.partyId);
                  }}
                >
                  Open the customer
                </Button>
              )}
            </PanelFoot>
          </Panel>
        </SplitMain>

        {/* ---- the invoice parameters, `lg:col-span-5` --------------------- */}
        <SplitSide span={5}>
          <Panel>
            <PanelHead label="Invoice parameters">
              <PanelTag>{stored?.status ?? 'Not saved yet'}</PanelTag>
            </PanelHead>
            <FieldGrid columns={2}>
              <Field label="Invoice number">
                <FieldLocked>{stored?.invoiceNumber ?? 'On issue'}</FieldLocked>
              </Field>
              <Field label="PO / contract ref">
                <FieldInput
                  label="Customer reference"
                  value={reference}
                  placeholder="Their purchase order"
                  onChange={setReference}
                />
              </Field>
              <Field label="Issue date">
                <FieldInput
                  label="Issue date"
                  type="date"
                  value={issueDate}
                  onChange={setIssueDate}
                />
              </Field>
              <Field label="Due date">
                <FieldLocked icon="calendar-blank">
                  {stored?.dueDate == null
                    ? (term?.name ?? 'From the term')
                    : formatDay(stored.dueDate)}
                </FieldLocked>
              </Field>
              <Field label="Base currency" span2>
                <FieldLocked>
                  <CurrencyMark>£</CurrencyMark> GBP — Pound sterling
                </FieldLocked>
              </Field>
            </FieldGrid>
            <PanelNote icon="info">
              The due date is set from the customer&rsquo;s payment term when the invoice is issued.
            </PanelNote>
          </Panel>
        </SplitSide>
      </Split>

      {/* ---- the line-item grid ------------------------------------------- */}
      <RecordsCard>
        <RecordsToolbar
          icon="list"
          title="Line items &amp; ledger distribution"
          count={`${String(lines.length)} ${lines.length === 1 ? 'item' : 'items'}`}
        />
        <LineGrid
          accounts={income}
          lines={lines}
          currency={currency}
          {...(saved === undefined ? {} : { stored: saved.lines })}
          onChange={setLines}
        />
        <Ribbon note="The ledger computes every figure; nothing on this page multiplies money.">
          <Button
            variant="secondary"
            icon={<Icon name="plus" size={14} />}
            onClick={() => {
              setLines([...lines, blankLine(lines.length)]);
            }}
          >
            Add line item
          </Button>
        </Ribbon>
      </RecordsCard>

      {/* ---- remittance on the left, the calculation on the right ---------- */}
      <Split>
        <SplitMain span={7}>
          {/*
            THE REMITTANCE MODULE, as Screen 05's reference draws it under the
            customer note — and as the ENTITY'S OWN SETTINGS hold it, not as
            four fields somebody types per invoice. Stitch's bank block has
            Institution, Account, Sort code and IBAN; `payment_details` is
            free text and newline-separated for the reason the migration gives:
            sort code, IBAN and routing number are not the same fields in the
            same countries, so the lines are rendered in the order they were
            configured rather than mapped onto four English labels.
          */}
          <Panel>
            <PanelHead label="Receiving bank details" icon="bank">
              {settings === null ? (
                <StatusChip tone="warning">Not configured</StatusChip>
              ) : (
                <PanelTag>{settings.tradingName ?? 'This entity'}</PanelTag>
              )}
            </PanelHead>
            {settings?.paymentDetails == null ? (
              <PanelNote icon="warning">
                This entity has no payment details configured, so the invoice will be rendered
                without them. Finance settings is where they are set; nothing is invented here.
              </PanelNote>
            ) : (
              <Panel variant="inset">
                <FieldGrid columns={2}>
                  {settings.paymentDetails
                    .split('\n')
                    .filter((line) => line.trim() !== '')
                    .map((line) => {
                      const [label, ...rest] = line.split(':');
                      return (
                        <Field key={line} label={rest.length === 0 ? 'Detail' : (label ?? '')}>
                          <FieldValue strong>
                            {rest.length === 0 ? line : rest.join(':').trim()}
                          </FieldValue>
                        </Field>
                      );
                    })}
                </FieldGrid>
              </Panel>
            )}
            <PanelNote icon="info">
              The entity&rsquo;s address, bank details and standing terms are printed from Finance
              settings, and the customer&rsquo;s address and VAT registration from the shared party
              record. Neither is typed here, so an invoice cannot disagree with the books it came
              from.
            </PanelNote>
          </Panel>
        </SplitMain>

        <SplitSide span={5}>
          <Panel>
            <PanelHead label="Financial calculation">
              <PanelTag>
                {stored === undefined ? 'Not yet computed' : 'Computed by the ledger'}
              </PanelTag>
            </PanelHead>
            <Totals>
              <TotalsRow
                label="Net taxable amount"
                emphasis
                amount={stored === undefined ? dash : moneyOf(stored.netTotal, currency)}
              />
              <TotalsRow
                label="Value added tax"
                sublabel={
                  stored === undefined || stored.status === 'draft'
                    ? 'Computed at the tax point, on issue'
                    : 'UK output VAT'
                }
                amount={stored === undefined ? dash : moneyOf(stored.taxTotal, currency)}
              />
            </Totals>
            <TotalsBanner
              label={`Total payable (${currency})`}
              amount={stored === undefined ? dash : moneyOf(stored.grossTotal, currency)}
              side={
                <>
                  <PanelTag>{term?.name ?? 'Entity default'}</PanelTag>
                  <span>
                    {stored?.dueDate == null ? 'Dated on issue' : formatDay(stored.dueDate)}
                  </span>
                </>
              }
            />
            <PanelNote icon="info">
              {stored === undefined
                ? 'Save the draft and the server returns the net, the tax and the total it computed. Nothing here multiplies quantity by price.'
                : 'These are the figures the server computed over the stored lines.'}
            </PanelNote>
          </Panel>
        </SplitSide>
      </Split>

      <AddPartySheet
        role="customer"
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        finance={gateway}
        parties={parties}
        legalEntityId={legalEntityId}
        onCreated={(created) => {
          // Select them, then ask the server what the list is now. The draft on
          // screen is untouched, which is the whole point.
          setPartyId(created.partyId);
          void gateway.customers(legalEntityId).then(setReread, () => {
            // A failed RE-READ is not a failed create. The customer exists;
            // leaving the list as it was is honest, and the next load shows
            // them. Inventing the row here would claim to know what was stored.
          });
        }}
      />
    </>
  );
}

// ---- 4. Credit notes --------------------------------------------------------

export function CreditNotesScreen({
  gateway,
  legalEntityId,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId?: string | undefined;
}) {
  const { state, reload } = useAsync<readonly CreditNote[]>(
    () => gateway.creditNotes(legalEntityId),
    [gateway, legalEntityId],
  );

  if (state.status === 'loading') return <LoadingState title="Loading credit notes…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  if (state.value.length === 0) {
    return (
      <Card>
        <CardHeader title="Credit notes" />
        <EmptyState
          icon="bank"
          title="No credit notes"
          description="A credit note corrects an issued invoice; an issued invoice is never edited."
        />
      </Card>
    );
  }

  return (
    <RecordsCard>
      <RecordsToolbar
        icon="bank"
        title="Credit notes"
        count={`${String(state.value.length)} in view`}
      />
      <RecordsScroll>
        <Table label="Credit notes">
          <thead>
            <Tr>
              <Th>Number</Th>
              <Th>Status</Th>
              <Th>Issued</Th>
              <Th>Reason</Th>
              <Th align="right">Gross</Th>
            </Tr>
          </thead>
          <tbody>
            {state.value.map((note) => (
              <Tr key={note.id}>
                <Td>{note.creditNoteNumber ?? 'Draft'}</Td>
                <Td>
                  <StatusChip tone={statusTone(note.status)}>{note.status}</StatusChip>
                </Td>
                <Td variant="secondary">{formatDay(note.issueDate)}</Td>
                <Td variant="secondary">{note.reasonCode}</Td>
                <Td variant="money">{moneyOf(note.grossTotal, note.currency)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </RecordsScroll>
    </RecordsCard>
  );
}

// ---- 5. Receipts and allocation --------------------------------------------

export function ReceiptsScreen({
  gateway,
  legalEntityId,
  canAllocate,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId?: string | undefined;
  readonly canAllocate: boolean;
}) {
  const { state, reload } = useAsync<{
    receipts: readonly SettlementDocument[];
    invoices: readonly Invoice[];
  }>(
    async () => ({
      receipts: await gateway.receipts(legalEntityId),
      invoices: await gateway.invoices(legalEntityId),
    }),
    [gateway, legalEntityId],
  );

  const [receiptId, setReceiptId] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [amount, setAmount] = useState('');
  const [outcome, setOutcome] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading receipts…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { receipts, invoices } = state.value;

  async function allocate() {
    setProblem(undefined);
    setOutcome(undefined);
    try {
      const result = await gateway.allocateReceivable({
        sourceKind: 'receipt',
        sourceId: receiptId,
        targetId: invoiceId,
        amount,
      });
      // THE SERVER'S ARITHMETIC, REPORTED. The browser did not work out what is
      // left; it is showing what the transaction decided under a row lock.
      setOutcome(
        `Applied. The invoice is ${result.targetStatus} with ${result.targetOutstanding} ` +
          `outstanding; ${result.sourceRemaining} remains on the receipt.`,
      );
      reload();
    } catch (error) {
      setProblem(refusalText(error, 'The allocation was refused.'));
    }
  }

  return (
    <div>
      <RecordsCard>
        <RecordsToolbar icon="bank" title="Receipts" count={`${String(receipts.length)} in view`} />
        {receipts.length === 0 ? (
          <EmptyState icon="bank" title="No receipts" description="Money received appears here." />
        ) : (
          <RecordsScroll>
            <Table label="Receipts">
              <thead>
                <Tr>
                  <Th>Number</Th>
                  <Th>Received</Th>
                  <Th align="right">Amount</Th>
                  <Th align="right">Applied</Th>
                  <Th align="right">Remaining</Th>
                </Tr>
              </thead>
              <tbody>
                {receipts.map((receipt) => (
                  <Tr key={receipt.id}>
                    <Td>{receipt.number ?? '—'}</Td>
                    <Td variant="secondary">{formatDay(receipt.occurredOn)}</Td>
                    <Td variant="money">{moneyOf(receipt.amount, receipt.currency)}</Td>
                    <Td variant="money">{moneyOf(receipt.allocated, receipt.currency)}</Td>
                    <Td variant="money">{moneyOf(receipt.remaining, receipt.currency)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </RecordsScroll>
        )}
      </RecordsCard>

      {canAllocate ? (
        <Card>
          <CardHeader
            title="Apply a receipt"
            subtitle="Part of a receipt may be applied. Applying more than is owed is refused."
          />
          {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
          {outcome !== undefined ? <Alert tone="success" title={outcome} /> : null}
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              void allocate();
            }}
          >
            <FormSection title="Allocation">
              <SelectField
                label="Receipt"
                value={receiptId}
                onChange={(event) => {
                  setReceiptId(event.target.value);
                }}
                options={receipts.map((receipt) => ({
                  value: receipt.id,
                  label: `${receipt.number ?? receipt.id.slice(0, 8)} — ${moneyOf(
                    receipt.remaining,
                    receipt.currency,
                  )} left`,
                }))}
                required
              />
              <SelectField
                label="Invoice"
                value={invoiceId}
                onChange={(event) => {
                  setInvoiceId(event.target.value);
                }}
                options={invoices
                  .filter((invoice) => invoice.status !== 'draft')
                  .map((invoice) => ({
                    value: invoice.id,
                    label: `${invoice.invoiceNumber ?? invoice.id.slice(0, 8)} — ${moneyOf(
                      invoice.grossTotal,
                      invoice.currency,
                    )}`,
                  }))}
                required
              />
              <TextField
                label="Amount, in minor units"
                value={amount}
                onChange={(event) => {
                  setAmount(event.target.value);
                }}
                required
              />
            </FormSection>
            <FormActions submitLabel="Apply" />
          </Form>
        </Card>
      ) : null}
    </div>
  );
}

// ---- 6. Screen 07 — ageing ---------------------------------------------------

/**
 * THE AGEING REPORT — `07_Receivables_Ageing/code.html`.
 *
 * The reference's anatomy: a control matrix, a FIVE-card band, a distribution
 * strip, then the ledger table pivoted one row per debtor with a footing row.
 *
 * **THE PIVOT IS A REGROUPING, NOT A SECOND ANSWER.** `/finance/receivables/
 * ageing` returns one row per party PER BAND, already through the record
 * predicate; this puts those rows into the reference's columns. The per-debtor
 * total and the footing row are `BigInt` sums of exactly those values and of
 * nothing else — see `figures.ts` for why that is allowed and where it stops.
 *
 * **THE DEBTOR IS NAMED, NOT IDENTIFIED.** The ageing carries a party id; the
 * name comes from the customer register, which has been through the same
 * predicate. A party the lookup does not cover keeps its identifier, because the
 * debt is real and hiding the row would understate what is owed.
 *
 * **OMITTED (§4E).** A risk tier, a dunning run, an export menu, an AI
 * collection forecast and a bad-debt provision. There is no scoring model, no
 * dunning engine, no export of this report and no IFRS 9 provision anywhere in
 * the product.
 */
export function AgeingScreen({
  gateway,
  side,
  onOpenParty,
}: {
  readonly gateway: FinanceGateway;
  readonly side: 'receivables' | 'payables';
  readonly onOpenParty?: ((partyId: string) => void) | undefined;
}) {
  const { state, reload } = useAsync<{
    buckets: readonly AgeingBucket[];
    names: ReadonlyMap<string, string>;
    limits: ReadonlyMap<string, string | null>;
  }>(async () => {
    const buckets =
      side === 'receivables' ? await gateway.receivablesAgeing() : await gateway.payablesAgeing();
    const parties =
      side === 'receivables'
        ? await gateway.customers().catch(() => [])
        : await gateway.suppliers().catch(() => []);
    return {
      buckets,
      names: new Map(parties.map((party) => [party.partyId, party.displayName])),
      limits: new Map(
        parties.map((party) => [party.partyId, 'creditLimit' in party ? party.creditLimit : null]),
      ),
    };
  }, [gateway, side]);

  const [query, setQuery] = useState('');

  if (state.status === 'loading') return <LoadingState title="Loading ageing…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { buckets, names, limits } = state.value;
  // The currency is the entity's; ageing is reported in it.
  const currency = 'GBP';
  const label = side === 'receivables' ? 'debtors' : 'creditors';

  const header = (
    <PageHeader
      actions={
        <FilterBar>
          <FilterControls>
            <span>As of today</span>
          </FilterControls>
        </FilterBar>
      }
    />
  );

  if (buckets.length === 0) {
    return (
      <>
        <PageHeader />
        <Card>
          <EmptyState
            icon="chart-bar"
            title="Nothing outstanding"
            description="Within your reach."
          />
        </Card>
      </>
    );
  }

  const inBucket = (bucket: string) =>
    sumMinor(buckets.filter((row) => row.bucket === bucket).map((row) => row.outstanding));
  const total = sumMinor(buckets.map((row) => row.outstanding));
  const overdueTotal = sumMinor(
    buckets.filter((row) => row.bucket !== 'current').map((row) => row.outstanding),
  );
  const older = sumMinor(
    buckets
      .filter((row) => row.bucket === 'overdue_61_90' || row.bucket === 'overdue_90_plus')
      .map((row) => row.outstanding),
  );

  /*
   * THE PIVOT. One row per party, its bands in the reference's column order, and
   * each cell the server's own figure for that party in that band.
   */
  const partyIds = [...new Set(buckets.map((row) => row.partyId))];
  const rows = partyIds
    .map((id) => {
      const mine = buckets.filter((row) => row.partyId === id);
      return {
        partyId: id,
        name: names.get(id) ?? id,
        total: sumMinor(mine.map((row) => row.outstanding)),
        held: mine.some((row) => row.disputed),
        documents: mine.reduce((count, row) => count + row.documents, 0),
        limit: limits.get(id) ?? null,
        bands: new Map(
          BUCKET_ORDER.map((bucket) => [
            bucket,
            sumMinor(mine.filter((row) => row.bucket === bucket).map((row) => row.outstanding)),
          ]),
        ),
      };
    })
    .filter((row) => query.trim() === '' || row.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <>
      {header}

      {/* FIVE BUCKETS IN ONE DESKTOP ROW — the reference's own count. */}
      <SummaryBand columns={5}>
        <SummaryCard
          icon="bank"
          tone="teal"
          label="Total outstanding"
          value={moneyOf(total, currency)}
          caption={`${String(partyIds.length)} ${label}`}
        />
        <SummaryCard
          icon="check-circle"
          tone="success"
          label="Current (not due)"
          value={moneyOf(inBucket('current'), currency)}
          badge={<SummaryBadge tone="success">Healthy</SummaryBadge>}
        />
        <SummaryCard
          icon="calendar-blank"
          tone="info"
          label="1–30 days overdue"
          value={moneyOf(inBucket('overdue_1_30'), currency)}
          badge={<SummaryBadge tone="info">Follow up</SummaryBadge>}
        />
        <SummaryCard
          icon="warning-circle"
          tone="warning"
          label="31–60 days overdue"
          value={moneyOf(inBucket('overdue_31_60'), currency)}
          badge={<SummaryBadge tone="warning">Escalated</SummaryBadge>}
        />
        <SummaryCard
          icon="warning"
          urgent
          label="61+ days overdue"
          value={moneyOf(older, currency)}
          badge={<SummaryBadge tone="danger">Critical</SummaryBadge>}
        />
      </SummaryBand>

      <Panel>
        <PanelHead title="Ageing distribution profile" icon="chart-bar">
          <Legend>
            {BUCKET_ORDER.map((bucket) => (
              <LegendItem key={bucket} tone={BUCKET_TONE[bucket] ?? 'neutral'}>
                {BUCKET_LABELS[bucket] ?? bucket}
              </LegendItem>
            ))}
          </Legend>
        </PanelHead>
        <Distribution
          parts={BUCKET_ORDER.map((bucket) => ({
            id: bucket,
            label: BUCKET_LABELS[bucket] ?? bucket,
            fraction: fractionOf(inBucket(bucket), total),
            tone: BUCKET_TONE[bucket] ?? 'neutral',
            title: `${BUCKET_LABELS[bucket] ?? bucket}: ${moneyOf(inBucket(bucket), currency)}`,
          }))}
        />
        <PanelFoot>
          <PanelNote icon="info">
            Every band is the server&rsquo;s own ageing of the same authorised relation. Disputed
            debt is still counted — removing it would understate what is owed by exactly the amount
            in argument.
          </PanelNote>
          <span>{moneyOf(overdueTotal, currency)} past due</span>
        </PanelFoot>
      </Panel>

      <RecordsCard>
        <FilterBar flush>
          <FilterSearch
            label={`Search ${label}`}
            placeholder={`Filter ${label} by name…`}
            value={query}
            onChange={setQuery}
          />
        </FilterBar>
        <RecordsScroll dense wrapNames>
          <Table label={side === 'receivables' ? 'Receivables ageing' : 'Payables ageing'}>
            <thead>
              {/*
                SEVEN COLUMNS, AND THE TWO THAT WENT.
                Stitch draws ten: the party, a total, five bands, a credit
                limit, a risk tier and a row of actions. There is no risk model,
                so the tier is out (§4E). The credit limit is out for a
                different reason — it fits nowhere: seven money columns at this
                product's "GBP 12,345.00" formatting need more than the 960
                pixels a 1280-wide window leaves, and a column pushed off the
                right edge of a card whose scrollbar the shell hides is a column
                nobody knows is missing. The limit is on Screens 02 and 03,
                where it is the subject rather than an aside.
              */}
              <Tr>
                <Th>{side === 'receivables' ? 'Customer' : 'Supplier'}</Th>
                <Th align="right">Total balance</Th>
                {BUCKET_ORDER.map((bucket) => (
                  <Th key={bucket} align="right">
                    {BUCKET_LABELS[bucket] ?? bucket}
                  </Th>
                ))}
              </Tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Tr key={row.partyId}>
                  <Td>
                    <EntityCell
                      mark={initialsOf(row.name)}
                      name={row.name}
                      /*
                        WHETHER THE DEBT IS BEING CHASED, on the row it belongs
                        to. The debt is here either way: disputed debt is
                        SEPARATED so a dunning run can leave it alone, never
                        removed — removing it would understate what is owed by
                        exactly the amount in argument.
                      */
                      sub={
                        row.held
                          ? `${String(row.documents)} documents · on hold`
                          : `${String(row.documents)} documents`
                      }
                      {...(onOpenParty === undefined
                        ? {}
                        : {
                            onOpen: () => {
                              onOpenParty(row.partyId);
                            },
                          })}
                    />
                  </Td>
                  <Td variant="money">{moneyOf(row.total, currency)}</Td>
                  {BUCKET_ORDER.map((bucket) => (
                    <Td key={bucket} variant="money">
                      {moneyOf(row.bands.get(bucket) ?? '0', currency)}
                    </Td>
                  ))}
                </Tr>
              ))}
            </tbody>
            <tfoot>
              <Tr>
                <Td>Ledger total</Td>
                <Td variant="money">{moneyOf(total, currency)}</Td>
                {BUCKET_ORDER.map((bucket) => (
                  <Td key={bucket} variant="money">
                    {moneyOf(inBucket(bucket), currency)}
                  </Td>
                ))}
              </Tr>
            </tfoot>
          </Table>
        </RecordsScroll>
        <RecordsFooter>
          <span>
            Showing <RecordsCount>{rows.length}</RecordsCount> of{' '}
            <RecordsCount>{partyIds.length}</RecordsCount> {label}
          </span>
          <span>Aged as the server computed it, at today&rsquo;s date</span>
        </RecordsFooter>
      </RecordsCard>
    </>
  );
}

// ---- 7. Screen 02 — customers ------------------------------------------------

/**
 * **CUSTOMERS — `02_Customers_List/code.html`.**
 *
 * The reference's anatomy: an action cluster, a four-card KPI band with trend
 * lines, a filter bar, then the dense register with its footer.
 *
 * THIS IS NOT A SECOND IDENTITY TABLE. The organisation lives in Party Master,
 * shared with CRM and everything after it; `finance_customer` holds only what
 * Finance owns — the terms, the credit limit and policy, the default coding, and
 * whether the relationship is on hold. The name in the first column is the
 * PARTY's name, joined in by the server.
 *
 * WHERE THE MONEY COLUMNS COME FROM. `/finance/customers` carries no balances,
 * so "outstanding" and "overdue" are read from the server's own ageing and
 * matched by party id — one request for the whole page rather than one per row,
 * and the same figures Screen 07 reports.
 *
 * OMITTED (§4E). Export and import menus, bulk selection, numbered pagination, a
 * risk-profile filter, a credit-exposure model, a photograph of an office and an
 * AI payment-propensity alert. None of them exists.
 */
export function CustomersScreen({
  gateway,
  legalEntityId,
  canCreate = false,
  onOpen,
  onNew,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId?: string | undefined;
  /** A courtesy. Adding a customer is two writes and the server guards both. */
  readonly canCreate?: boolean;
  /** Screen 02 → Screen 03. Absent, the name is plain text rather than a dead link. */
  readonly onOpen?: ((partyId: string) => void) | undefined;
  readonly onNew?: (() => void) | undefined;
}) {
  const { state, reload } = useAsync<{
    customers: readonly FinanceCustomer[];
    terms: readonly FinancePaymentTerm[];
    ageing: readonly AgeingBucket[];
  }>(
    async () => ({
      customers: await gateway.customers(legalEntityId),
      // The names behind the term ids. Without an entity there is nothing to
      // ask for, and an id in a column is not an answer anybody can read.
      terms: legalEntityId === undefined ? [] : await gateway.paymentTerms(legalEntityId),
      ageing: await gateway.receivablesAgeing().catch(() => []),
    }),
    [gateway, legalEntityId],
  );

  const [query, setQuery] = useState('');
  const [standing, setStanding] = useState('all');

  if (state.status === 'loading') return <LoadingState title="Loading customers…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { customers, terms, ageing } = state.value;
  const currency = 'GBP';
  const termName = (id: string | null) =>
    id === null ? '—' : (terms.find((term) => term.id === id)?.name ?? '—');

  const outstandingOf = (partyId: string) =>
    sumMinor(ageing.filter((row) => row.partyId === partyId).map((row) => row.outstanding));
  const overdueOf = (partyId: string) =>
    sumMinor(
      ageing
        .filter((row) => row.partyId === partyId && row.bucket !== 'current')
        .map((row) => row.outstanding),
    );

  const header = (
    <PageHeader
      actions={
        canCreate && onNew !== undefined ? (
          <Button variant="primary" icon={<Icon name="plus" size={14} />} onClick={onNew}>
            New customer
          </Button>
        ) : undefined
      }
    />
  );

  if (customers.length === 0) {
    return (
      <>
        {header}
        <Card>
          <EmptyState
            icon="users-three"
            title="No customers"
            description="Customers within your reach appear here. A customer is an organisation in your shared records with a Finance relationship for this entity."
          />
        </Card>
      </>
    );
  }

  const active = customers.filter((customer) => customer.isActive && !customer.isOnHold);
  const onHold = customers.filter((customer) => customer.isOnHold);
  const total = sumMinor(ageing.map((row) => row.outstanding));
  const overdue = sumMinor(
    ageing.filter((row) => row.bucket !== 'current').map((row) => row.outstanding),
  );
  const debtors = new Set(ageing.map((row) => row.partyId)).size;

  const needle = query.trim().toLowerCase();
  const shown = customers.filter((customer) => {
    if (standing === 'active' && (!customer.isActive || customer.isOnHold)) return false;
    if (standing === 'hold' && !customer.isOnHold) return false;
    if (standing === 'inactive' && customer.isActive) return false;
    if (needle === '') return true;
    return [customer.displayName, customer.billingEmail ?? '']
      .join(' ')
      .toLowerCase()
      .includes(needle);
  });

  return (
    <>
      {header}

      <SummaryBand>
        <SummaryCard
          icon="users-three"
          tone="teal"
          label="Active customers"
          value={String(active.length)}
          caption={`${String(customers.length)} relationships in all`}
        />
        <SummaryCard
          icon="bank"
          tone="info"
          label="Outstanding receivables"
          value={moneyOf(total, currency)}
          caption={`${String(debtors)} with a balance`}
        />
        <SummaryCard
          icon="warning"
          urgent
          label="Overdue balance"
          value={moneyOf(overdue, currency)}
          caption="Past its due date"
        />
        <SummaryCard
          icon="lock-simple"
          tone="warning"
          label="On credit hold"
          value={String(onHold.length)}
          caption="Invoicing refused or warned"
        />
      </SummaryBand>

      <RecordsCard>
        <FilterBar flush>
          <FilterSearch
            label="Search customers"
            placeholder="Search customers by name or billing email…"
            value={query}
            onChange={setQuery}
          />
          <FilterControls>
            <FilterSelect
              label="Standing"
              value={standing}
              onChange={setStanding}
              options={[
                { value: 'all', label: 'All accounts' },
                { value: 'active', label: 'Active' },
                { value: 'hold', label: 'On credit hold' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
          </FilterControls>
        </FilterBar>
        {/*
          NO ACTIONS COLUMN, BECAUSE THE NAME IS THE ACTION. Stitch draws a
          "View" button and an overflow menu on every row; the only thing behind
          either is the customer's own page, and the name in the first column
          already opens it. A column that repeats a link costs the standing chip
          beside it its place at 1280, which is a worse trade than it sounds:
          whether an account is on credit hold is the one thing on this row that
          stops an invoice being issued.
        */}
        <RecordsScroll dense>
          <Table label="Customers">
            <thead>
              <Tr>
                <Th>Customer</Th>
                <Th>Billing email</Th>
                <Th>Terms</Th>
                <Th align="right">Credit limit</Th>
                <Th align="right">Outstanding</Th>
                <Th align="right">Overdue</Th>
                <Th>Standing</Th>
              </Tr>
            </thead>
            <tbody>
              {shown.map((customer) => {
                const due = overdueOf(customer.partyId);
                return (
                  <Tr key={`${customer.legalEntityId}:${customer.partyId}`}>
                    <Td>
                      {/*
                        A LINK ONLY WHERE THERE IS SOMEWHERE TO GO. The customer
                        page needs a legal entity to scope its figures to, and a
                        caller reaching none has no page to open.
                      */}
                      <EntityCell
                        mark={initialsOf(customer.displayName)}
                        name={customer.displayName}
                        sub={customer.billingCurrency ?? 'Entity currency'}
                        {...(onOpen === undefined
                          ? {}
                          : {
                              onOpen: () => {
                                onOpen(customer.partyId);
                              },
                            })}
                      />
                    </Td>
                    <Td variant="secondary">{customer.billingEmail ?? '—'}</Td>
                    <Td>
                      <CodeChip>{termName(customer.paymentTermId)}</CodeChip>
                    </Td>
                    <Td variant="money">
                      {/*
                        NO LIMIT IS NOT A LIMIT OF ZERO. A dash says the server
                        holds no figure; a 0 would say they may owe nothing.
                      */}
                      {customer.creditLimit === null
                        ? '—'
                        : formatMoney(customer.creditLimit, customer.billingCurrency ?? 'GBP')}
                      {/*
                        WHAT THE LIMIT DOES, under the limit itself. A number
                        alone does not say whether passing it stops an invoice
                        or merely warns, and those are opposite facts to somebody
                        about to raise one. The server decides which; this says
                        which it decided.
                      */}
                      {customer.creditLimit !== null && (
                        <RowDetail>
                          {customer.creditPolicy === 'block'
                            ? 'Invoicing is blocked'
                            : 'A warning is shown'}
                        </RowDetail>
                      )}
                    </Td>
                    <Td variant="money">{moneyOf(outstandingOf(customer.partyId), currency)}</Td>
                    <Td variant="money">{moneyOf(due, currency)}</Td>
                    <Td>
                      {/*
                        THREE SERVER FLAGS, NOT A JUDGEMENT. On hold is the one
                        that stops an invoice being issued, so it is said first
                        and said in warning colour; inactive is a relationship
                        nobody has ended but nobody is using.
                      */}
                      {customer.isOnHold ? (
                        <StatusChip tone="warning">On credit hold</StatusChip>
                      ) : customer.isActive ? (
                        <StatusChip tone="success">Active</StatusChip>
                      ) : (
                        <StatusChip tone="neutral">Inactive</StatusChip>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </RecordsScroll>
        <RecordsFooter>
          <span>
            Showing <RecordsCount>{shown.length}</RecordsCount> of{' '}
            <RecordsCount>{customers.length}</RecordsCount> customers
          </span>
          <span>
            {customers.length === shown.length
              ? 'Every customer within your reach'
              : 'Filtered in this browser; nothing is hidden from the count'}
          </span>
        </RecordsFooter>
      </RecordsCard>
    </>
  );
}
